/* 1a — Watercraft homepage banner, desktop web.
   Ported from `01-placements/1a_homepage-banner-desktop-web.html` in the
   Watercraft handoff pack. Built as markup rather than dropped in as the
   flat PNG from `04-png/` so the copy stays selectable, scalable and
   translatable and the CTAs are real links.

   Marketing surface, so it keeps the dark Watercraft identity and the solid
   neon CTA rather than native swimbooker styling. Desktop only — the pack
   ships 1b–1e as the mobile banners, so this hides below `wc-1a`'s
   breakpoint instead of reflowing. */
import React from 'react';
import QRCode from 'react-qr-code';
import WatercraftCardWall from './WatercraftCardWall';
import { wcColors, wcFonts, wcOnAccent, wcSolidTint } from './tokens';
import { ReactComponent as Wordmark } from '../../assets/images/watercraft/wc_wordmark.svg';
import '../../assets/css/watercraft_banner.css';

const WATERCRAFT_URL = 'https://watercraft.app';

// TODO: swap for the real store listings once Watercraft is published, and
// replace the text lockups below with the official Apple/Google artwork —
// the pack ships placeholder lockups.
const APP_STORE_URL = WATERCRAFT_URL;
const GOOGLE_PLAY_URL = WATERCRAFT_URL;

function AppleIcon() {
  return (
    <svg
      viewBox='0 0 24 24'
      style={{ width: '15px', height: '18px', display: 'block' }}
      fill={wcColors.textPrimary}
      aria-hidden='true'
    >
      <path d='M17.05 12.53c-.02-2.2 1.79-3.26 1.87-3.31-1.02-1.49-2.61-1.7-3.17-1.72-1.35-.14-2.63.79-3.31.79-.68 0-1.79-.77-2.94-.75-1.51.02-2.9.88-3.68 2.23-1.57 2.72-.4 6.74 1.13 8.95.75 1.08 1.64 2.24 2.81 2.2 1.13-.04 1.56-.73 2.93-.73 1.36 0 1.75.73 2.94.71 1.21-.02 1.98-1.1 2.72-2.19.85-1.25 1.2-2.46 1.22-2.52-.03-.01-2.34-.9-2.36-3.56z' />
      <path d='M14.9 6.02c.6-.72 1-1.72.89-2.72-.86.03-1.9.57-2.52 1.29-.55.63-1.03 1.65-.9 2.62.96.07 1.93-.49 2.53-1.19z' />
    </svg>
  );
}

function PlayIcon() {
  return (
    <svg
      viewBox='0 0 24 24'
      style={{ width: '15px', height: '18px', display: 'block' }}
      aria-hidden='true'
    >
      <path
        d='M4 2.4 L18.6 12 L4 21.6 Z'
        fill='none'
        stroke={wcColors.textPrimary}
        strokeWidth='1.8'
        strokeLinejoin='round'
      />
    </svg>
  );
}

function StoreLink({ href, icon, children }) {
  return (
    <a
      className='wc-1a__store'
      href={href}
      target='_blank'
      rel='noopener noreferrer'
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '7px',
        whiteSpace: 'nowrap',
      }}
    >
      {icon}
      <span
        style={{
          fontFamily: wcFonts.body,
          fontSize: '13.5px',
          fontWeight: '500',
          color: wcColors.textPrimary,
        }}
      >
        {children}
      </span>
    </a>
  );
}

export default function WatercraftBanner() {
  return (
    /* Full-bleed like `.sb-pro-ad-container`: the ground spans the viewport
       and the content is capped at --content-max-width and centred, so the
       banner lines up with the swimbooker+ section. */
    <div className='wc-1a'>
      <div className='wc-1a__inner'>
        <div
          className='wc-1a__grid'
          style={{
            position: 'relative',
            /* No clipping here: the panel's halo has to reach the banner's
               top and bottom edges, and the grid sits 24px inside them. The
               card wall clips its own cards, so nothing needs it. */
            display: 'grid',
            gridTemplateColumns: '1fr 540px',
          }}
        >
          <div
            aria-hidden='true'
            style={{
              position: 'absolute',
              left: '-140px',
              top: '-90px',
              width: '520px',
              height: '420px',
              borderRadius: '100%',
              background:
                'radial-gradient(circle,rgba(61,255,106,.34) 0%,rgba(61,255,106,.08) 48%,rgba(61,255,106,0) 72%)',
              filter: 'blur(46px)',
              pointerEvents: 'none',
            }}
          />
          <div
            aria-hidden='true'
            style={{
              position: 'absolute',
              left: '120px',
              bottom: '-160px',
              width: '460px',
              height: '380px',
              borderRadius: '100%',
              background:
                'radial-gradient(circle,rgba(61,255,106,.22) 0%,rgba(26,92,42,.12) 50%,rgba(61,255,106,0) 74%)',
              filter: 'blur(52px)',
              pointerEvents: 'none',
            }}
          />

          <div
            className='wc-1a__panel-outer'
            style={{ position: 'relative', padding: '34px 30px' }}
          >
            <div
              className='wc-1a__panel'
              style={{
                position: 'relative',
                background:
                  'linear-gradient(140deg,rgba(61,255,106,.13) 0%,rgba(61,255,106,.05) 42%,rgba(238,244,235,.03) 100%)',
                backdropFilter: 'blur(18px)',
                WebkitBackdropFilter: 'blur(18px)',
                border: '1px solid rgba(61,255,106,.24)',
                borderRadius: '20px',
                /* The design sets 38px; a little more vertical room here to
                 give the panel more presence beside the card wall. */
                padding: '72px 36px',
                boxShadow:
                  '0 20px 50px rgba(0,0,0,.35),inset 0 1px 0 rgba(238,244,235,.1)',
              }}
            >
              <span
                style={{
                  position: 'absolute',
                  top: '0',
                  right: '36px',
                  transform: 'translateY(-50%)',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '7px',
                  /* Opaque, not a tint: the pill is pulled up onto the panel's
                   top edge, so a translucent fill would show that edge
                   running through it. Flattened against the ground it comes
                   out a hair off the pack's #0F2A17. */
                  background: wcSolidTint(wcColors.primary, 0.25),
                  border: '1px solid rgba(61,255,106,.6)',
                  color: wcColors.accent,
                  fontFamily: wcFonts.mono,
                  fontSize: '10px',
                  fontWeight: '600',
                  letterSpacing: '.2em',
                  textTransform: 'uppercase',
                  padding: '6px 13px',
                  borderRadius: '100px',
                  whiteSpace: 'nowrap',
                  boxShadow: '0 4px 18px rgba(61,255,106,.22)',
                }}
              >
                <span
                  style={{
                    width: '6px',
                    height: '6px',
                    borderRadius: '100px',
                    background: wcColors.accent,
                    display: 'block',
                  }}
                />
                New
              </span>

              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  marginBottom: '26px',
                }}
              >
                <Wordmark
                  role='img'
                  aria-label='Watercraft'
                  style={{
                    height: '62px',
                    width: '98px',
                    display: 'block',
                    color: wcColors.textPrimary,
                  }}
                />
              </div>

              <h2
                className='wc-1a__headline'
                style={{
                  fontFamily: wcFonts.display,
                  fontSize: '46px',
                  lineHeight: '1.05',
                  fontWeight: '600',
                  color: wcColors.textPrimary,
                  margin: '0 0 18px',
                  letterSpacing: '-.015em',
                }}
              >
                You book the session.
                <span
                  style={{
                    display: 'block',
                    fontStyle: 'italic',
                    fontWeight: '400',
                    color: wcColors.textReading,
                  }}
                >
                  Watercraft remembers the details.
                </span>
              </h2>

              <p
                style={{
                  fontFamily: wcFonts.body,
                  fontSize: '16px',
                  lineHeight: '1.65',
                  color: wcColors.textBody,
                  fontWeight: '300',
                  margin: '0 0 30px',
                  maxWidth: '440px',
                }}
              >
                A private field tool for what you work out on the bank. Catches,
                sessions, spots, conditions. Default private, and it gives it
                all back when you ask.
              </p>

              <div
                className='wc-1a__actions'
                style={{ display: 'flex', alignItems: 'flex-end', gap: '30px' }}
              >
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '18px',
                    alignItems: 'flex-start',
                  }}
                >
                  <a
                    className='wc-1a__cta'
                    href={WATERCRAFT_URL}
                    target='_blank'
                    rel='noopener noreferrer'
                    style={{
                      display: 'inline-block',
                      background: wcColors.accent,
                      color: wcOnAccent,
                      fontFamily: wcFonts.body,
                      fontSize: '16px',
                      fontWeight: '700',
                      padding: '17px 40px',
                      borderRadius: '11px',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    Get Watercraft FREE
                  </a>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '14px',
                      flexWrap: 'nowrap',
                    }}
                  >
                    {/* textSecondary rather than the textMuted the pack used
                      here: at 9.5px, textMuted lands at 2.62:1 on this panel,
                      under WCAG AA. textMuted stays in the card wall, which is
                      decorative and hidden from assistive tech. */}
                    <span
                      style={{
                        fontFamily: wcFonts.mono,
                        fontSize: '9.5px',
                        letterSpacing: '.16em',
                        textTransform: 'uppercase',
                        color: wcColors.textSecondary,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      Download
                    </span>
                    <span
                      style={{
                        width: '1px',
                        height: '16px',
                        background: wcColors.border,
                        display: 'block',
                      }}
                    />
                    <StoreLink href={APP_STORE_URL} icon={<AppleIcon />}>
                      App Store
                    </StoreLink>
                    <StoreLink href={GOOGLE_PLAY_URL} icon={<PlayIcon />}>
                      Google Play
                    </StoreLink>
                  </div>
                </div>

                {/* The pack renders this QR through api.qrserver.com at request
                  time; generated locally instead so nothing on the homepage
                  depends on a third party, and it stays sharp at any size. */}
                <div
                  className='wc-1a__qr'
                  style={{
                    flex: 'none',
                    marginLeft: 'auto',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '9px',
                  }}
                >
                  <div
                    style={{
                      width: '104px',
                      height: '104px',
                      background: wcColors.textPrimary,
                      borderRadius: '10px',
                      padding: '7px',
                      boxSizing: 'border-box',
                      boxShadow: '0 10px 26px rgba(0,0,0,.35)',
                    }}
                  >
                    <QRCode
                      value={WATERCRAFT_URL}
                      bgColor={wcColors.textPrimary}
                      fgColor={wcOnAccent}
                      level='M'
                      style={{
                        width: '100%',
                        height: '100%',
                        display: 'block',
                      }}
                      title='QR code linking to watercraft.app'
                    />
                  </div>
                  <span
                    style={{
                      fontFamily: wcFonts.mono,
                      fontSize: '9.5px',
                      letterSpacing: '.14em',
                      textTransform: 'uppercase',
                      color: '#8AAB8A',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    watercraft.app
                  </span>
                </div>
              </div>
            </div>
          </div>

          <WatercraftCardWall />
        </div>
      </div>
    </div>
  );
}
